import React from 'react';

import { BasePage } from '../../components/base-page';
import { Button, Stack, Typography } from '@mui/material';
import { useLazyQuery, useMutation } from '@apollo/client/react';

import { useAlert } from '@dwelvio/react-alert';
import { useAuth } from '@dwelvio/auth-react';
import {
  DateFormatTypeLabelArray,
  IUserData,
  LanguageTypeLabelArray,
  NumberFormatTypeLabelArray,
  TimeFormatTypeLabelArray,
} from '../../types';

import { LabelAndValue } from '../../components/label-and-value';
import { ListWithRemove } from '../../components/list-with-remove';
import {
  DateFormatType,
  LanguageType,
  NumberFormatType,
  TimeFormatType,
  GetUserByIdDocument,
  GetUserByIdQuery,
  GetUserByIdQueryVariables,
  UpdateUserDocument,
  UpdateUserMutation,
  UpdateUserMutationVariables,
} from '../../generated/graphql';
import { EditIcon, PasswordIcon } from '../../components/icons';
import { EditProfileDialog } from './components/edit-profile-dialog';
import { useParams, useSearchParams } from 'react-router';

import { FormSelect } from '../../components/form-select';
import { ChangePasswordDialog } from './components/change-password-dialog';

export function ProfilePage() {
  const { show } = useAlert();
  const { user, refresh, changePassword } = useAuth<IUserData>();
  const { userId } = useParams<{ userId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showProfileEditDialog, setShowProfileEditDialog] = React.useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = React.useState(false);

  const refreshAuth = searchParams.get('refresh') === '1';

  const [getUser, { data: userData, loading, error: getUserError }] = useLazyQuery<
    GetUserByIdQuery,
    GetUserByIdQueryVariables
  >(GetUserByIdDocument);

  React.useEffect(() => {
    if (userId) {
      getUser({ variables: { userId } });
    }
  }, [userId, user?.id, getUser]);

  React.useEffect(() => {
    if (getUserError) {
      show.error('Nepodarilo sa načítať profil.', getUserError.message);
    }
  }, [getUserError, show]);

  React.useEffect(() => {
    if (userData && !userData.getUserById) {
      show.error('Nepodarilo sa načítať profil.');
    }
  }, [userData, show]);

  React.useEffect(() => {
    if (refreshAuth) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('refresh');
        return newParams;
      });
      refresh();
    }
  }, [refresh, getUser]);

  const userD = userData?.getUserById;

  const [updateUser] = useMutation<UpdateUserMutation, UpdateUserMutationVariables>(UpdateUserDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa upraviť profil.', e.message);
    },
  });

  return (
    <BasePage title="Profil" loading={loading} errorMsg={userD ? undefined : 'Nepodarilo sa načítať profil.'}>
      <Stack direction="column" spacing={1} useFlexGap maxWidth={560}>
        <LabelAndValue label="Email (meno používateľa)" value={userD?.email ?? 'xxx'} />
        <LabelAndValue label="Celé meno" value={userD?.fullName ?? 'xxx'} />
        <LabelAndValue label="Telefón" value={userD?.phone ?? 'xxx'} />

        <Stack direction="row" spacing={2} useFlexGap>
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setShowProfileEditDialog(true)}>
            Upraviť profil
          </Button>
          <Button variant="outlined" startIcon={<PasswordIcon />} onClick={() => setShowChangePasswordDialog(true)}>
            Zmeniť heslo
          </Button>
        </Stack>

        <Typography variant="h3">Nastavenia</Typography>
        <FormSelect
          label="Jazyk"
          items={LanguageTypeLabelArray}
          value={userD?.lang}
          direction="row"
          onChange={(e) => {
            const lang = e.target.value as LanguageType;
            if (userId) {
              updateUser({ variables: { userId, input: { lang } } });
            }
          }}
        />

        <FormSelect
          label="Formát čísel"
          items={NumberFormatTypeLabelArray}
          value={userD?.fmtNum}
          direction="row"
          onChange={(e) => {
            const fmtNum = e.target.value as NumberFormatType;
            if (userId) {
              updateUser({ variables: { userId, input: { fmtNum } } });
            }
          }}
        />

        <FormSelect
          label="Formát dátumov"
          items={DateFormatTypeLabelArray}
          value={userD?.fmtDate}
          direction="row"
          onChange={(e) => {
            const fmtDate = e.target.value as DateFormatType;
            if (userId) {
              updateUser({ variables: { userId, input: { fmtDate } } });
            }
          }}
        />

        <FormSelect
          label="Formát časov"
          items={TimeFormatTypeLabelArray}
          value={userD?.fmtTime}
          direction="row"
          onChange={(e) => {
            const fmtTime = e.target.value as TimeFormatType;
            if (userId) {
              updateUser({ variables: { userId, input: { fmtTime } } });
            }
          }}
        />

        <Typography variant="h3">Máte prístup k účtom</Typography>
        <ListWithRemove<IUserData['accounts'][0]>
          items={user?.accounts}
          keyFn={(i) => i.id}
          labelFn={(i) => i.name}
          hideRemoveButtonFn={(i) => true}
          spacing={1}
        />
      </Stack>
      {userD && (
        <EditProfileDialog
          user={userD}
          open={showProfileEditDialog}
          onClose={() => setShowProfileEditDialog(false)}
          onUpdate={(input) =>
            updateUser({
              variables: { userId: userD.id, input },
              onCompleted: () => {
                show.info('Profil bol úspešne upravený.');
                setShowProfileEditDialog(false);
              },
              onError: (e: Error) => {
                show.error('Nepodarilo sa upraviť profil.', e.message);
              },
            })
          }
        />
      )}
      {showChangePasswordDialog && (
        <ChangePasswordDialog
          open={showChangePasswordDialog}
          username={userD?.email ?? ''}
          onClose={() => setShowChangePasswordDialog(false)}
          onSubmit={(newPassword, oldPassword) => {
            if (userId) {
              changePassword(oldPassword, newPassword)
                .then(() => {
                  show.info('Heslo bolo úspešne zmenené.');
                  setShowChangePasswordDialog(false);
                })
                .catch((e) => {
                  show.error('Nepodarilo sa zmeniť heslo.', e.message);
                });
            }
          }}
        />
      )}
    </BasePage>
  );
}
